import useLocalisedCountryName from '@app/src/hooks/useLocalisedCountryName'
import type { FacetItem } from '@app/src/pages/ResourceCollection'
import OptionWithLabel from '@app/src/pages/ResourceCollection/Filters/OptionWithLabel'
import type { AutocompleteGetTagProps, AutocompleteRenderOptionState } from '@mui/material'
import { Chip } from '@mui/material'
import React, { useCallback, useMemo } from 'react'

const useLocalisedCountryFilter = () => {
  const { getLocalisedCountryName } = useLocalisedCountryName()

  const countryNameCache = useMemo(() => new Map<string, string>(), [getLocalisedCountryName])

  const getDisplayName = useCallback(
    (isoCode: string): string => {
      if (!isoCode) return '-'

      const cached = countryNameCache.get(isoCode)
      if (cached !== undefined) {
        return cached
      }

      const localizedName = getLocalisedCountryName(isoCode) || isoCode
      countryNameCache.set(isoCode, localizedName)
      return localizedName
    },
    [getLocalisedCountryName, countryNameCache],
  )

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: FacetItem,
    state: AutocompleteRenderOptionState,
  ) => {
    const localizedCountryName = getDisplayName(option.label)

    return (
      <OptionWithLabel
        key={option.label}
        props={props}
        label={localizedCountryName || option.label}
        multiple
        selected={state.selected}
      />
    )
  }

  const renderTags = (value: FacetItem[], getTagProps: AutocompleteGetTagProps) => {
    return value.map((option, index) => {
      const localizedCountryName = getDisplayName(option.label)

      return (
        <Chip
          {...getTagProps({ index })}
          key={option.label}
          label={localizedCountryName || option.label}
          size="small"
        />
      )
    })
  }

  const stripAccentsAndLowercase = (str: string) =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

  const customFilterOption = (options: FacetItem[], { inputValue }: { inputValue: string }) => {
    const normalizedInput = stripAccentsAndLowercase(inputValue)

    return options.filter(option => {
      const isoCode = option.label
      const localizedName = getDisplayName(isoCode)

      return (
        stripAccentsAndLowercase(isoCode).includes(normalizedInput) ||
        stripAccentsAndLowercase(localizedName).includes(normalizedInput)
      )
    })
  }

  const customSort = (a: FacetItem, b: FacetItem) => {
    const nameA = getDisplayName(a.label)
    const nameB = getDisplayName(b.label)
    return nameA.localeCompare(nameB)
  }

  return { renderOption, renderTags, customFilterOption, customSort }
}

export default useLocalisedCountryFilter
